import { getDay, addDays, getMonth, getYear } from '@e3dc-react/shell/Libs/date'
import { LineItem } from '../models'
import { DayOfWeek, ProductGeneralType, ProductType, ShopSystemConfigurationTypes } from '../types'
import { FixFlex } from '../models/basic-system.model'
import { getGermanyHolidaysByYear } from '@e3dc-react/shell/Libs/Holidays/index'
import { SHOP_CHANNEL_SHIPPING_DAYS } from './desired-shipping-date-channel-days'
import { ShopChannelType } from './shop-channel-rules'

interface DisabledMonth {
  month: number
  year: number
}

export interface SystemConfigurationFixFlex {
  quantity: number
  fixFlex: FixFlex
  type: ShopSystemConfigurationTypes
}

/*
 * This function returns offset of desired shipping date based on the type of product
 */
export const getBasketDesiredShippingDateOffset = (products?: LineItem[], channelType: ShopChannelType = ShopChannelType.ShopE3dc): number => {
  const shippingDays = SHOP_CHANNEL_SHIPPING_DAYS[channelType]
  for (const product of products || []) {
    if (product.type === ProductGeneralType.QNTM_SYSTEM_CONFIGURATOR) {
      return shippingDays.qntm
    }

    if (product.type === ProductGeneralType.SYSTEM_CONFIGURATOR) {
      return shippingDays.systemConfigurator
    }

    if (product.type === ProductGeneralType.PRODUCT && product.payload.e3dc.productType === ProductType.RetrofitBattery) {
      return shippingDays.systemConfigurator
    }
  }

  return shippingDays.product
}
/*
 * Checks if the selected month and year remain the same after accounting for shipping days
 * Returns true if the month changes, indicating unavailability for orders with the desired shipping days
 */
export const isShippingForDifferentMonth = (
  selectedMonth?: string,
  selectedYear?: string,
  channelType: ShopChannelType = ShopChannelType.ShopE3dc
): boolean => {
  const shippingDays = SHOP_CHANNEL_SHIPPING_DAYS[channelType]
  const currentDate = new Date()
  const newDate = addDays(currentDate, shippingDays.systemConfigurator)

  if (getMonth(currentDate) === Number(selectedMonth) && getYear(currentDate) === Number(selectedYear)) {
    if (getMonth(newDate) !== getMonth(currentDate)) {
      return true
    }
  }

  return false
}

/*
 * This function get all the Shipping Months that should be disabled on the date picker
 */
export const getDisabledSystemConfigureShippingMonths = (items: SystemConfigurationFixFlex[] | undefined): DisabledMonth[] => {
  const disabledMonthsMap = new Map<string, DisabledMonth>()
  if (!items) return []

  const months = getAllMonthsFromConfigurators(items)

  for (const monthKey of months) {
    const [yearStr, monthStr] = monthKey.split('-')
    const year = Number.parseInt(yearStr, 10)
    const month = Number.parseInt(monthStr, 10)

    const shouldDisable = items.some(item => isSystemConfiguratorDisabledForMonth(item, year, month))

    const fixFlexConflict = checkFixFlexConflict(items, year, month)

    if (shouldDisable || fixFlexConflict) {
      disabledMonthsMap.set(monthKey, { year, month })
    }
  }

  return Array.from(disabledMonthsMap.values())
}

/*
 * This function just checks conditions if the Configurator fullfills the rules for the month
 */
const isSystemConfiguratorDisabledForMonth = (item: SystemConfigurationFixFlex, year: number, month: number): boolean => {
  const { quantity, fixFlex } = item
  if (!fixFlex?.[year]?.[month]) return false

  const { fixAvailable, flexAvailable } = fixFlex[year][month]

  if (fixAvailable >= quantity) return false
  if (fixAvailable === 0 && flexAvailable >= quantity) return false

  return true
}

/*
 *  * This function get all months across all configurators that have fixFlex data.
 */
const getAllMonthsFromConfigurators = (items: SystemConfigurationFixFlex[] | undefined): Set<string> => {
  const months = new Set<string>()
  if (!items) return months

  for (const item of items) {
    if (!item.fixFlex) continue

    for (const [year, yearData] of Object.entries(item.fixFlex)) {
      for (const month in yearData) {
        months.add(`${year}-${month}`)
      }
    }
  }

  return months
}

/*
 * This function checks if all system configurators fullfill fix or flex condition for specific month.
 */
const checkFixFlexConflict = (items: SystemConfigurationFixFlex[], year: number, month: number): boolean => {
  let allProductsCanUseFix = true
  let allProductsCanUseFlex = true

  for (const { fixFlex, quantity } of items) {
    const monthData = fixFlex?.[year]?.[month]
    if (!monthData) return true

    if (monthData.fixAvailable < quantity) allProductsCanUseFix = false

    if (monthData.fixAvailable !== 0 || monthData.flexAvailable < quantity) allProductsCanUseFlex = false

    if (!allProductsCanUseFix && !allProductsCanUseFlex) return true
  }

  return !(allProductsCanUseFix || allProductsCanUseFlex)
}
/*
 * This function returns the public and bank holidays for the current and next year.
 */
export const fetchCurrentAndNextYearHolidayDates = (): Date[] => {
  const currentDate = new Date()
  /** Since getHolidays limits you by year, we are getting for the upcoming year also */
  const currentYearHolidays = getGermanyHolidaysByYear(currentDate.getFullYear())
  const nextYearHolidays = getGermanyHolidaysByYear(currentDate.getFullYear() + 1)

  const allHolidays = [...currentYearHolidays, ...nextYearHolidays]
  return allHolidays
}

/*
 * This function checks if date passed is weekend day and returns a boolean.
 */
export const isDateWeekend = (date: Date): boolean => {
  const dayOfWeek = getDay(date)
  return dayOfWeek === DayOfWeek.Saturday || dayOfWeek === DayOfWeek.Sunday
}
